import { EditGroup } from "../../Components/EditGroup"
import { BlockField, BlockRow } from "../../Components/Block"
import { ConfirmDialog } from "../../Components/ConfirmDialog"
import { Menu, MenuItemDividerProps } from "../../Components/Menu"
import { IAccountUserLevel } from "../../Types"
import { useAccountUserEdit } from "./useAccountUserEdit"
import { AccountUserEditViewProps } from "./Params"

// ---------------------------------------------------------------------------
// One row per account member: name/email/shortname/status are always
// read-only (editing another member's profile is out of scope for #979 (tracker #827)); the
// level select and the "..." menu (deactivate/reactivate/remove) are the only
// editable affordances. Mirrors ShipUser/EditView.tsx's row + Menu structure.
// ---------------------------------------------------------------------------

export function AccountUserEditView({ accountUserLevelOptions, onChanged, userInfo, initError }: AccountUserEditViewProps) {
  const { app, t, edited, onEdited, error, updateAccountUser, deactivate, reactivate, remove, pendingAction, cancelPendingAction, confirmPendingAction } = useAccountUserEdit({ userInfo, onChanged, initError });

  const isSelf = userInfo.user.id === app.user?.id;
  const isDisabled = userInfo.level === IAccountUserLevel.disabled;
  const deactivatedClass = isDisabled ? "user_deactivated" : "";

  const menuItems = [
    ...(!isDisabled ? [
      { id: "deactivate_user", onClick: () => { deactivate(); return true; }, title: t("deactivate_user") },
    ] : [
      { id: "reactivate_user", onClick: () => { reactivate(); return true; }, title: t("reactivate_user") },
    ]),
    MenuItemDividerProps,
    { id: "remove_user", onClick: () => { remove(); return true; }, title: t("remove_user") },
  ];

  return (
    <>
      <EditGroup key={userInfo.user.id} changed={edited} editableFields={[{ name: "accountLevel", type: "int" }]} onSave={updateAccountUser} error={error && t("error_" + error)} className="editgroup_boxed">
        <BlockRow className="ShipUserEditView-row">
          <BlockField label={t("user_name")} className="block_field_small_20">
            <input name="name" defaultValue={userInfo.user.name} readOnly className={deactivatedClass} />
          </BlockField>
          <BlockField label={t("user_email")} className="block_field_small_20">
            <input name="email" defaultValue={userInfo.user.email} readOnly className={deactivatedClass} />
          </BlockField>
          <BlockField label={t("user_shortname")} className="block_field_small_8">
            <input name="shortname" defaultValue={userInfo.user.shortname} readOnly className={deactivatedClass} />
          </BlockField>
          <BlockField label={t("account_user_level")} className="block_field_small_8">
            {(isSelf || isDisabled) ? (
              <input name="accountLevel" value={t(`account_user_level_${IAccountUserLevel[userInfo.level]}`)} readOnly className={deactivatedClass} />
            ) : (
              // Uncontrolled: the select only exists while `!isDisabled`, so it always
              // (re)mounts fresh whenever `userInfo.level` last changed via deactivate/
              // reactivate. A `value=` binding to the (unchanged-by-onChange) prop would
              // have React snap the DOM back on every render, before EditGroup's effect
              // gets to read the just-picked value out of FormData.
              <select defaultValue={userInfo.level} name="accountLevel" onChange={onEdited}>
                {accountUserLevelOptions}
              </select>
            )}
          </BlockField>
          <BlockField label={t("user_status")} className="block_field_small_8">
            <input name="user_status" defaultValue={t(`user_status_${userInfo.status}`)} readOnly className={deactivatedClass} />
          </BlockField>
          {!isSelf && (
            <BlockField className="button_user_options user_options">
              <Menu button="..." buttonClass="button" global={true} items={menuItems} />
            </BlockField>
          )}
        </BlockRow>
      </EditGroup>

      {pendingAction && (
        <ConfirmDialog
          awaitConfirm={true}
          title={t(`user_${pendingAction}_confirm_title`)}
          message={t(`user_${pendingAction}_confirm_message`)}
          confirmLabel={t(`user_${pendingAction}_confirm_accept`)}
          cancelLabel={t(`user_${pendingAction}_confirm_cancel`)}
          style="alarm"
          onConfirm={confirmPendingAction}
          onCancel={cancelPendingAction}
        />
      )}
    </>
  );
}
